// Reembolsos — Portal Vendedor: enganches que se regresan al cliente por ventas a crédito canceladas.
// Sale de `GET /portal/pending-refunds`; el efectivo se marca entregado con `POST …/{sessionId}/deliver`.
import { useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog, EmptyState, ErrorState, Loading, usePermissions } from '@/kit';
import { ApiError, userMessageOf } from '@/api/errors';
import type { SellerPendingRefundItem } from '@fiado/type-kit/bin/retailWizard/dtos/SellerPendingRefundItem';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { KpiCell } from '@/shared/patterns/KpiBar';
import { ScreenBand } from '@/shared/patterns/ScreenBand';
import { useStoreRefunds } from './refunds/api/useStoreRefunds';
import { useDeliverRefund } from './refunds/api/useDeliverRefund';
import { RefundsTable } from './refunds/components/RefundsTable';
import { deliverFailureOf } from './refunds/lib/deliverRefundErrors';
import { refundPaymentsLabel, refundReference } from './refunds/lib/refundsFormat';
import { REFUNDS_PERMISSIONS } from './refunds/permissions';

export function SalesRefunds() {
    const { hasPermission } = usePermissions();
    const query = useStoreRefunds();
    const deliver = useDeliverRefund();
    const [target, setTarget] = useState<SellerPendingRefundItem | null>(null);
    const [blockedNotes, setBlockedNotes] = useState<ReadonlyMap<string, string>>(new Map());
    const data = query.data;

    if (query.isPending) return <Loading size="lg" text="Cargando los reembolsos" />;
    if (!data) {
        // Con un 403 reintentar no cambia nada.
        const forbidden = query.error instanceof ApiError && query.error.httpStatus === 403;
        return (
            <ErrorState
                title="No se pudieron cargar los reembolsos"
                description={userMessageOf(query.error)}
                onRetry={
                    forbidden
                        ? undefined
                        : () => {
                              void query.refetch();
                          }
                }
            />
        );
    }

    const confirmDeliver = (refund: SellerPendingRefundItem) => {
        deliver.mutate(refund.sessionId, {
            onSuccess: () => {
                toast.success(`Devolución del reembolso ${refundReference(refund)} anotada`);
                setTarget(null);
            },
            onError: (error) => {
                const failure = deliverFailureOf(error);
                toast.error(failure.message);
                if (failure.action === 'hide') {
                    setBlockedNotes((prev) =>
                        new Map(prev).set(refund.sessionId, failure.note ?? ''),
                    );
                }
                if (failure.action !== 'retry') setTarget(null);
            },
        });
    };

    return (
        <div className="flex h-full min-h-0 flex-col">
            <ScreenBand className="gap-0">
                <KpiCell
                    item={{
                        label: 'Pendientes',
                        value: data.pendingCount,
                        tone: data.pendingCount > 0 ? 'warning' : 'default',
                    }}
                />
                <KpiCell
                    item={{
                        label: 'Por regresar',
                        value: formatMoneyCents(data.pendingTotalCents),
                    }}
                />
            </ScreenBand>

            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-auto pt-3">
                {query.isError && (
                    <p role="status" className="text-[11px] text-muted-foreground">
                        No se pudo actualizar. Lo que ves es la última lectura.
                    </p>
                )}
                <p className="text-[12px] text-muted-foreground">
                    Regresa el enganche al cliente. Ves los pendientes de tu tienda y los entregados
                    de los últimos 30 días.
                </p>
                {data.refunds.length === 0 ? (
                    <EmptyState
                        title="No hay reembolsos"
                        description="Cuando se cancele una venta a crédito con el enganche cobrado, aparecerá aquí."
                    />
                ) : (
                    <RefundsTable
                        refunds={data.refunds}
                        canDeliver={hasPermission(REFUNDS_PERMISSIONS.deliver)}
                        blockedNotes={blockedNotes}
                        onDeliver={setTarget}
                    />
                )}
            </div>

            <ConfirmDialog
                open={target !== null}
                onOpenChange={(open) => {
                    if (!open && !deliver.isPending) setTarget(null);
                }}
                title="Anotar devolución"
                description={
                    target
                        ? `Confirma que le devolviste ${formatMoneyCents(target.refundPendingCents)} (${refundPaymentsLabel(target).label}) al cliente de la venta ${refundReference(target)}.`
                        : undefined
                }
                confirmLabel="Sí, lo devolví"
                loading={deliver.isPending}
                onConfirm={() => {
                    if (target) confirmDeliver(target);
                }}
            />
        </div>
    );
}
